import React, { useEffect, useState, useCallback } from 'react';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import FilterBar from '../../components/common/FilterBar';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { requestAPI, partyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const fmtErr = (d, fb) => { if (!d) return fb; if (typeof d === 'string') return d; if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0]}: ${e.msg}` : JSON.stringify(e)).join('; '); return d.msg || d.message || JSON.stringify(d); };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 5 };

const STATUSES   = ['REQUESTED', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT', 'CRITICAL'];
const SERVICES   = ['WOUND_CARE', 'INJECTIONS', 'ELDERLY_CARE', 'PHYSIOTHERAPY', 'POST_SURGERY_CARE', 'MEDICATION_MANAGEMENT', 'VITAL_MONITORING', 'PALLIATIVE_CARE', 'MATERNAL_CARE', 'PEDIATRIC_CARE', 'MENTAL_HEALTH', 'REHABILITATION'];

const FILTER_DEF = [
  { key: 'status',   label: 'Status',   type: 'select', options: STATUSES },
  { key: 'service',  label: 'Service',  type: 'select', options: SERVICES },
  { key: 'priority', label: 'Priority', type: 'select', options: PRIORITIES },
];
const BLANK_F = { status: '', service: '', priority: '' };

export default function CareRequests() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isPatientOnly = roles.includes('PATIENT') && !roles.some(r => ['ADMIN', 'DOCTOR', 'NURSE', 'BILLING'].includes(r));
  const canPlan = roles.includes('DOCTOR') || roles.includes('ADMIN');
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const PER = 50;
  const [filters, setFilters] = useState(BLANK_F);
  const [patients, setPatients] = useState([]);

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [cf, setCf] = useState({ patient_id: '', service_type: 'WOUND_CARE', priority: 'MEDIUM', description: '', location_address: '', location_lat: null, location_lng: null, preferred_date: '' });
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoErr, setGeoErr] = useState('');

  // Uber-style: one tap fills the address + coordinates from the device GPS.
  const useMyLocation = async () => {
    setGeoBusy(true); setGeoErr('');
    try {
      const { getPosition, reverseGeocode, fmtCoords, accuracyLabel } = await import('../../utils/geo');
      const { lat, lng, accuracy } = await getPosition();
      const address = await reverseGeocode(lat, lng);
      setCf(f => ({ ...f, location_lat: lat, location_lng: lng,
                    location_address: address || fmtCoords(lat, lng) }));
      const warn = accuracyLabel(accuracy);
      if (warn) setGeoErr(`Location is ${warn}.`);
    } catch (e) { setGeoErr(e.message); }
    finally { setGeoBusy(false); }
  };
  const [cErr, setCErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit
  const [editRow, setEditRow] = useState(null);
  const [ef, setEf]           = useState({ status: 'REQUESTED', priority: 'MEDIUM' });
  const [eErr, setEErr]       = useState('');
  const [eSaving, setESaving] = useState(false);

  // Cancel
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelLoading, setCancel] = useState(false);

  const buildParams = useCallback(() => {
    const p = { page, per_page: PER };
    if (filters.status)  p.status       = filters.status;
    if (filters.service) p.service_type = filters.service;
    // Patients explicitly request only their own rows. The backend scopes
    // regardless — this guards against any stale token/session in the tab.
    if (isPatientOnly && user?.party_id) p.patient_id = user.party_id;
    return p;
  }, [filters, page, isPatientOnly, user]);

  const load = useCallback(({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    requestAPI.list(buildParams())
      .then(r => {
        let data = r.data || [];
        // Belt-and-braces: in patient mode never RENDER rows that aren't
        // the signed-in patient's, whatever the response contained.
        if (isPatientOnly && user?.party_id) {
          data = data.filter(row => String(row.patient_id) === String(user.party_id));
        }
        setRows(data);
        setTotal(r.meta?.total || 0);
      })
      .catch(() => { if (!silent) { setRows([]); setTotal(0); } })
      .finally(() => { if (!silent) setLoading(false); });
  }, [buildParams, isPatientOnly, user]);

  useEffect(() => { load(); }, [load]);
  // Keep the list live: a request submitted from the mobile app appears here
  // within seconds — no manual reload (silent refresh, rows never flash).
  useAutoRefresh(useCallback(() => load({ silent: true }), [load]), 15000);
  useEffect(() => { setPage(1); }, [filters]);
  useEffect(() => {
    if (isPatientOnly) return; // patients can't (and don't need to) list other patients
    partyAPI.list({ role: 'PATIENT', per_page: 200 }).then(r => setPatients(r.data || []));
  }, [isPatientOnly]);

  const visible = rows.filter(r => {
    if (filters.priority && r.priority !== filters.priority) return false;
    return true;
  });

  const handleCreate = async e => {
    e.preventDefault(); setSaving(true); setCErr('');
    try {
      await requestAPI.create({
        patient_id:      isPatientOnly ? user.party_id : cf.patient_id,
        service_type:    cf.service_type,
        priority:        cf.priority,
        description:     cf.description || undefined,
        location_address: cf.location_address || undefined,
        location_lat:    cf.location_lat ?? undefined,
        location_lng:    cf.location_lng ?? undefined,
        preferred_date:  cf.preferred_date || undefined,
      });
      setShowCreate(false);
      setCf({ patient_id: '', service_type: 'WOUND_CARE', priority: 'MEDIUM', description: '', location_address: '', location_lat: null, location_lng: null, preferred_date: '' });
      load();
    } catch (err) { setCErr(fmtErr(err.response?.data?.detail, 'Failed to create care request')); }
    finally { setSaving(false); }
  };

  const openEdit = row => { setEditRow(row); setEf({ status: row.status, priority: row.priority }); setEErr(''); };
  const handleEdit = async e => {
    e.preventDefault(); setESaving(true); setEErr('');
    try {
      await requestAPI.update(editRow.request_id, { status: ef.status, priority: ef.priority });
      setEditRow(null); load();
    } catch (err) { setEErr(fmtErr(err.response?.data?.detail, 'Failed to update')); }
    finally { setESaving(false); }
  };

  const handleCancel = async () => {
    setCancel(true);
    try { await requestAPI.cancel(cancelRow.request_id); setCancelRow(null); load(); }
    catch {} finally { setCancel(false); }
  };

  const priColor = p => ({ LOW: '#f1f5f9', MEDIUM: '#fef9c3', HIGH: '#fed7aa', URGENT: '#fecaca', CRITICAL: '#fca5a5' }[p] || '#f1f5f9');

  const columns = [
    { key: 'preferred_date',   label: 'Date',     render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'patient_id',       label: 'Patient',  render: (_, r) => isPatientOnly ? (user?.name || 'You') : (r.patient_name || r.patient_id?.slice(0, 8) || '—') },
    { key: 'service_type',     label: 'Service',  render: v => (v || '—').replace(/_/g, ' ') },
    { key: 'description',      label: 'Description', render: v => v?.length > 40 ? v.slice(0, 40) + '…' : (v || '—') },
    { key: 'location_address', label: 'Location', render: v => v?.length > 30 ? v.slice(0, 30) + '…' : (v || '—') },
    { key: 'assigned_doctor_id', label: 'Doctor', render: (_, r) => r.assigned_doctor_name || (r.assigned_doctor_id ? r.assigned_doctor_id.slice(0, 8) : '—') },
    { key: 'assigned_nurse_id',  label: 'Nurse',  render: (_, r) => r.assigned_nurse_name || (r.assigned_nurse_id ? r.assigned_nurse_id.slice(0, 8) : '—') },
    { key: 'priority',         label: 'Priority', render: v => <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: priColor(v) }}>{v}</span> },
    { key: 'status',           label: 'Status',   render: v => <Badge value={v} /> },
    {
      key: '_actions', label: 'Actions', render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          {canPlan && row.status === 'REQUESTED' && (
            <button
              title="Schedule an assessment visit — patient and clinician are filled automatically"
              onClick={() => navigate(`/visits?create=1&request=${row.request_id}&patient=${row.patient_id}&pname=${encodeURIComponent(row.patient_name || '')}`)}
              style={btnSm('#8b5cf6')}
            >📅 Visit</button>
          )}
          {canPlan && row.status === 'REQUESTED' && (
            <button
              title="Create care plan — activating it auto-dispatches the closest skill-matched nurse"
              onClick={() => navigate(`/care-plans?create=1&request=${row.request_id}&patient=${row.patient_id}&pname=${encodeURIComponent(row.patient_name || '')}`)}
              style={btnSm('#10b981')}
            >📋 Plan</button>
          )}
          {!isPatientOnly && <button onClick={() => openEdit(row)} style={btnSm('#94335a')} disabled={['CANCELLED', 'COMPLETED'].includes(row.status)}>✏️</button>}
          {(!isPatientOnly || String(row.patient_id) === String(user?.party_id)) && (
            <button onClick={() => setCancelRow(row)} style={btnSm('#ef4444')} disabled={['CANCELLED', 'COMPLETED'].includes(row.status)}>✕</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>Care Requests</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>{isPatientOnly ? 'Request home care and track your requests.' : `${total} requests · showing ${visible.length}`}</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnPrimary('#f59e0b')}>+ New Request</button>
      </div>

      <FilterBar filters={FILTER_DEF} values={filters} onChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))} onClear={() => setFilters(BLANK_F)} />

      {loading ? <div style={{ color: '#8b7681', padding: 20 }}>Loading…</div> : <Table columns={columns} data={visible} />}
      <Pagination page={page} total={total} per={PER} onPage={setPage} />

      {/* ── Create Modal ── */}
      {showCreate && (
        <Modal title="New Care Request" onClose={() => setShowCreate(false)} fullscreen>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
          {cErr && <ErrBox msg={cErr} />}
          <form onSubmit={handleCreate}>
            <Grid3>
              {isPatientOnly ? (
                <div>
                  <label style={lbl}>Requesting for</label>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 16, fontSize: 13, fontWeight: 600, color: '#2b1b22' }}>{user?.name} (you)</span>
                </div>
              ) : (
                <div>
                  <label style={lbl}>Patient *</label>
                  <select style={inp} value={cf.patient_id} onChange={e => setCf(f => ({ ...f, patient_id: e.target.value }))} required>
                    <option value="">— Select patient —</option>
                    {patients.map(p => <option key={p.party_id} value={p.party_id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <SelF label="Service *" k="service_type" form={cf} set={setCf} opts={SERVICES} />
              <SelF label="Priority" k="priority" form={cf} set={setCf} opts={PRIORITIES} />
            </Grid3>
            <div style={{ margin: '14px 0' }}>
              <label style={lbl}>Description</label>
              <textarea style={{ ...inp, minHeight: 80 }} value={cf.description} onChange={e => setCf(f => ({ ...f, description: e.target.value }))} />
            </div>
            <Grid2>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <label style={lbl}>Location / Address</label>
                  <button type="button" onClick={useMyLocation} disabled={geoBusy} style={{
                    background: '#fff', border: '1px solid #7b1e3c', borderRadius: 8,
                    padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#7b1e3c',
                    cursor: geoBusy ? 'wait' : 'pointer', marginBottom: 6,
                  }}>
                    {geoBusy ? 'Locating…' : '📍 Use my location'}
                  </button>
                </div>
                <input style={inp} value={cf.location_address}
                  onChange={e => setCf(f => ({ ...f, location_address: e.target.value, location_lat: null, location_lng: null }))} />
                {cf.location_lat != null && (
                  <div style={{ fontSize: 11, color: '#1d7a4d', marginTop: 4 }}>
                    ✓ GPS attached ({cf.location_lat}, {cf.location_lng}) — the nearest nurse can be routed to you
                  </div>
                )}
                {geoErr && <div style={{ fontSize: 11, color: '#b71c1c', marginTop: 4 }}>{geoErr}</div>}
              </div>
              <F label="Preferred Date" k="preferred_date" type="date" form={cf} set={setCf} />
            </Grid2>
            <FmtActions saving={saving} onCancel={() => setShowCreate(false)} label="Submit Request" color="#f59e0b" />
          </form>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editRow && (
        <Modal title="Update Request" onClose={() => setEditRow(null)}>
          {eErr && <ErrBox msg={eErr} />}
          <form onSubmit={handleEdit}>
            <Grid2>
              <SelF label="Status" k="status" form={ef} set={setEf} opts={STATUSES} />
              <SelF label="Priority" k="priority" form={ef} set={setEf} opts={PRIORITIES} />
            </Grid2>
            <FmtActions saving={eSaving} onCancel={() => setEditRow(null)} label="Save" color="#10b981" />
          </form>
        </Modal>
      )}

      {/* ── Cancel Confirm ── */}
      {cancelRow && (
        <ConfirmDialog
          message="Cancel this care request?"
          detail="The patient will need to submit a new request."
          onConfirm={handleCancel}
          onCancel={() => setCancelRow(null)}
          loading={cancelLoading}
          confirmLabel="Cancel Request"
        />
      )}
    </div>
  );
}

const btnPrimary = bg => ({ padding: '9px 18px', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' });
const btnSm = bg => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' });
const ErrBox = ({ msg }) => <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{msg}</div>;
const Grid2 = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>;
const Grid3 = ({ children }) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>{children}</div>;
const F = ({ label, k, type = 'text', form, set, required }) => (
  <div>
    <label style={lbl}>{label}</label>
    <input style={inp} type={type} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))} required={required} />
  </div>
);
const SelF = ({ label, k, form, set, opts }) => (
  <div>
    <label style={lbl}>{label}</label>
    <select style={inp} value={form[k]} onChange={e => set(f => ({ ...f, [k]: e.target.value }))}>
      <option value="">— Select —</option>
      {opts.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
    </select>
  </div>
);
const FmtActions = ({ saving, onCancel, label, color = '#94335a' }) => (
  <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
    <button type="submit" disabled={saving} style={{ padding: '9px 22px', background: color, color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving…' : label}</button>
    <button type="button" onClick={onCancel} style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
  </div>
);
const Pagination = ({ page, total, per, onPage }) => {
  const pages = Math.ceil(total / per); if (pages <= 1) return null;
  return <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20 }}>
    <button onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pgBtn}>‹</button>
    <span style={{ padding: '6px 12px', fontSize: 13, color: '#475569' }}>Page {page}/{pages}</span>
    <button onClick={() => onPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={pgBtn}>›</button>
  </div>;
};
const pgBtn = { padding: '6px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, cursor: 'pointer' };
